import React, { useEffect, useState } from "react";
import Loader from "../../../common/loader";
import LeadTableView from "./leadTableView";
import LeadCardView from "./leadCardView";
import AddLeadModal from "./addLead";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { IconButton } from "@mui/material";
import {
  Button,
  Paper,
  InputBase,
  Snackbar,
  Alert,
  Pagination,
  Typography,
  Box,
} from "@mui/material";
import {
  Search as SearchIcon,
  NoteAdd as NoteAddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { getLeads, deleteLead, inviteLeadUser } from "../../../services/lead.service";
import { getLoginUserData } from "../../../services/account.service";
import InviteLeadModal from "../../../common/inviteLeadConfirm";
import AddCustomerModal from "../customerDetails/addCustomer";
import TaskFormDialog from "../../business/tasks/TaskFormDialog";
import { useLocation, useNavigate } from "react-router-dom";

const Leads: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Card view on mobile/tablet, table on desktop

  const [viewMode, setViewMode] = useState<'card' | 'table'>(isMobile ? 'card' : 'table');
  const [isLoading, setIsLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    search: "",
    businessId: "",
    sortBy: "followUpDate",
    sortOrder: "DESC",
    status: ""
  });
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [openModal, setOpenModal] = useState(false);
  const [editLeadDetails, setEditLeadDetails] = useState<any | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });
  const [inviteModal, setInviteModal] = useState({
    open: false,
    lead: null as any | null,
  });
  const [customerModal, setCustomerModal] = useState({
    open: false,
    lead: null as any | null,
  });
  const [taskModal, setTaskModal] = useState({
    open: false,
    lead: null as any | null,
  });
  const [user, setUser] = useState<any>(null);

  const handleInviteLeadClick = (lead: any) => {
    setInviteModal({ open: true, lead });
  };

  const handleConvertLeadClick = (lead: any) => {
    setCustomerModal({ open: true, lead });
  };

  const handleAddTaskClick = (lead: any) => {
    setTaskModal({ open: true, lead });
  };

  const confirmInviteLead = async () => {
    if (!inviteModal.lead) return;
    try {
      await inviteLeadUser({ id: inviteModal.lead.id });
      showSnackbar("success", "Invitation sent successfully!");
      fetchLeads(query);
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to send invitation.");
    } finally {
      setInviteModal({ open: false, lead: null });
    }
  };
  const totalPages = Math.ceil(paginationData.total / paginationData.limit);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await getLoginUserData();
        setUser(user);
        if (user?.id) {
          const updatedQuery = {
            ...query,
            businessId: user?.businessId ? user?.businessId : user.id,
          };
          setQuery(updatedQuery);
        }
      } catch (error: any) {
        showSnackbar("error", error?.message || "Failed to load leads.");
      } finally {
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.businessId) fetchLeads(query);
    }, 700);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search, query.status]);

  // Update view mode when screen size changes
  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'table');
  }, [isMobile]);

  const fetchLeads = async (params: any) => {
    setIsLoading(true);
    try {
      const res = await getLeads(params);
      setLeads(res.data);
      setPaginationData({ page: res.page, limit: res.limit, total: res.total });
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to fetch leads.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLead = async (lead: any) => {
    try {
      await deleteLead(lead.id);
      showSnackbar("success", "Lead deleted successfully!");
      fetchLeads(query);
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to delete lead.");
    }
  };

  const editLead = (lead: any) => {
    if (lead) {
      setOpenModal(true);
      setEditLeadDetails(lead);
    }
  };

  const showSnackbar = (
    severity: "success" | "error" | "info",
    message: string
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery((prev) => ({ ...prev, search: event.target.value }));
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (query.businessId) {
        fetchLeads(query);
      }
    }
  };

  const handleStatusChange = (event: any) => {
    const status = event.target.value;
    const updatedQuery = { ...query, status, page: 1 };
    setQuery(updatedQuery);
  };

  const handlePageChange = (_: any, value: number) => {
    const updatedQuery = { ...query, page: value };
    setQuery((prev) => (updatedQuery));
    fetchLeads(updatedQuery);
  };

  useEffect(() => {
    fetchLeads(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.sortBy, query.sortOrder]);

  useEffect(() => {
    const openLeadId = (location.state as any)?.openLeadId;
    if (!openLeadId || !leads.length || openModal) return;

    const lead = leads.find((item: any) => item.id === openLeadId);
    if (lead) {
      editLead(lead);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, location.state, openModal]);

  return (
    <div>
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 py-2 px-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 min-w-0">
            <Paper
              component="form"
              className="flex items-center border border-gray-300 rounded-full px-2 flex-1"
              elevation={0}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
              }}
            >
              <SearchIcon className="text-gray-400 ml-2" />
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search lead"
                value={query.search}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
              {query.search && (
                <IconButton
                  size="small"
                  onClick={() => setQuery((prev) => ({ ...prev, search: "" }))}
                  sx={{ p: 0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
            <FormControl size="small" sx={{ minWidth: 170, maxWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={query.status}
                label="Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Public Lead">Public Lead</MenuItem>
                <MenuItem value="Invited">Invited</MenuItem>
                <MenuItem value="Registered">Registered Lead</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="flex items-center justify-center lg:justify-start flex-wrap gap-3">
             <Button
              variant="contained"
              startIcon={<NoteAddIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: { xs: "0.875rem", md: "1rem" },
                minWidth: { xs: "100%", md: "auto" },
              }}
            >
              Create Lead
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden mr-2">
                <IconButton
                  onClick={() => setViewMode("card")}
                  className={`!p-2 ${viewMode === "card" ? "bg-gray-100" : ""}`}
                  size="small"
                >
                  <ViewModuleIcon
                    className={viewMode === "card" ? "text-black" : "text-gray-500"}
                    fontSize="small"
                  />
                </IconButton>
                <IconButton
                  onClick={() => setViewMode("table")}
                  className={`!p-2 ${viewMode === "table" ? "bg-gray-100" : ""}`}
                  size="small"
                >
                  <ViewListIcon
                    className={viewMode === "table" ? "text-black" : "text-gray-500"}
                    fontSize="small"
                  />
                </IconButton>
              </div>
          </div>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {leads.length === 0 ? (
              <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-pulse">
                    <SearchRoundedIcon
                      className="text-gray-400"
                      sx={{ fontSize: 48 }}
                    />
                  </div>
                </div>
                <Typography variant="h6" className="mt-4 text-gray-700">
                  No Leads Found
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  You can try adjusting your search or add a new lead to get
                  started.
                </Typography>
              </Box>
            ) : (
              viewMode === 'card' ? (
                <LeadCardView
                  leads={leads}
                  onEdit={editLead}
                  onDelete={handleDeleteLead}
                  onInvite={handleInviteLeadClick}
                  onConvert={handleConvertLeadClick}
                  onAddTask={handleAddTaskClick}
                />
              ) : (
                <LeadTableView
                  leads={leads}
                  onEdit={editLead}
                  onDelete={handleDeleteLead}
                  onInvite={handleInviteLeadClick}
                  onConvert={handleConvertLeadClick}
                  onAddTask={handleAddTaskClick}
                  onFetch={(sortBy, sortOrder) =>
                    setQuery((prev) => ({ ...prev, sortBy, sortOrder : sortOrder.toUpperCase() }))
                  }
                  query={query}
                />
              )
            )}
            {leads.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
                <Pagination
                  count={totalPages}
                  page={paginationData.page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  size="small"
                />
                <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                  Showing {(paginationData.page - 1) * paginationData.limit + 1}{" "}
                  to{" "}
                  {Math.min(
                    paginationData.page * paginationData.limit,
                    paginationData.total
                  )}{" "}
                  of {paginationData.total} results
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <AddLeadModal
        open={openModal}
        handleClose={(event) => {
          setOpenModal(false);
          setEditLeadDetails(null);
          if (event && event === "reload") {
            fetchLeads(query);
          }
        }}
        editMode={!!editLeadDetails}
        leadData={editLeadDetails || undefined}
      />
      <InviteLeadModal
        open={inviteModal.open}
        email={inviteModal.lead?.email || ""}
        onConfirm={confirmInviteLead}
        onClose={() => setInviteModal({ open: false, lead: null })}
      />
      <AddCustomerModal
        open={customerModal.open}
        handleClose={(event) => {
          setCustomerModal({ open: false, lead: null });
          if (event && event === "reload") {
            fetchLeads(query);
          }
        }}
        businessId={user?.businessId || user?.id || ""}
        editMode={false}
        customerData={customerModal.lead ? {
          firstName: customerModal.lead.firstName,
          middleName: customerModal.lead.middleName,
          lastName: customerModal.lead.lastName,
          email: customerModal.lead.email,
          phone: customerModal.lead.phone,
          street: customerModal.lead.businessStreet,
          city: customerModal.lead.businessCity,
          state: customerModal.lead.businessState,
          zipcode: customerModal.lead.businessZipcode,
        } : undefined}
      />
      <TaskFormDialog
        open={taskModal.open}
        lead={taskModal.lead}
        onClose={(action) => {
          setTaskModal({ open: false, lead: null });
          if (action === "reload") {
            showSnackbar("success", "Task added successfully.");
          }
        }}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Leads;
